import { useState } from 'react';
import { Users, Filter, Plus } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/common/Card';
import Button from '@/components/common/Button';
import Badge from '@/components/common/Badge';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import Modal from '@/components/common/Modal';
import { useAccounts } from '@/hooks/useAccounts';
import { useRules } from '@/hooks/useRules';
import AccountCard from '@/components/accounts/AccountCard';
import AccountForm from '@/components/accounts/AccountForm';
import RuleCard from '@/components/rules/RuleCard';
import RuleForm from '@/components/rules/RuleForm';

function AccountsSection() {
  const { data: accounts, isLoading } = useAccounts();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);

  const handleAdd = () => {
    setEditingAccount(null);
    setIsModalOpen(true);
  };

  const handleEdit = (account) => {
    setEditingAccount(account);
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setEditingAccount(null);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><Users className="h-5 w-5" />Accounts</CardTitle>
            <CardDescription>Manage your IMAP email accounts</CardDescription>
          </div>
          <Button onClick={handleAdd} className="flex items-center gap-2"><Plus className="h-4 w-4" />Add Account</Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? <LoadingSpinner /> : accounts && accounts.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {accounts.map(account => (
              <AccountCard key={account.id} account={account} onEdit={handleEdit} />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 border rounded-lg">
            <p className="text-muted-foreground mb-4">No accounts configured yet.</p>
            <Button onClick={handleAdd} className="flex items-center gap-2 mx-auto"><Plus className="h-4 w-4" />Add Your First Account</Button>
          </div>
        )}
      </CardContent>
      <Modal isOpen={isModalOpen} onClose={handleClose} title={editingAccount ? 'Edit Account' : 'Add Account'} size="lg">
        <AccountForm account={editingAccount} onSuccess={handleClose} onCancel={handleClose} />
      </Modal>
    </Card>
  );
}

function RulesSection() {
  const { data: rules, isLoading } = useRules();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  const handleAdd = () => {
    setEditingRule(null);
    setIsModalOpen(true);
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setEditingRule(null);
  };

  const sortedRules = rules ? [...rules].sort((a, b) => (b.priority || 0) - (a.priority || 0)) : [];
  const enabledCount = sortedRules.filter(r => r.enabled).length;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><Filter className="h-5 w-5" />Rules</CardTitle>
            <CardDescription>Configure email filtering rules</CardDescription>
          </div>
          <Button onClick={handleAdd} className="flex items-center gap-2"><Plus className="h-4 w-4" />Add Rule</Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-3 mb-4 text-sm">
          <span className="text-muted-foreground">{sortedRules.length} total</span>
          <Badge variant="success">{enabledCount} enabled</Badge>
        </div>
        {isLoading ? <LoadingSpinner /> : sortedRules.length > 0 ? (
          <div className="space-y-3">
            {sortedRules.map(rule => (
              <RuleCard key={rule.id} rule={rule} onEdit={handleEdit} />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 border rounded-lg">
            <p className="text-muted-foreground mb-4">No rules configured yet.</p>
            <Button onClick={handleAdd} className="flex items-center gap-2 mx-auto"><Plus className="h-4 w-4" />Add Your First Rule</Button>
          </div>
        )}
      </CardContent>
      <Modal isOpen={isModalOpen} onClose={handleClose} title={editingRule ? 'Edit Rule' : 'Add Rule'} size="lg">
        <RuleForm rule={editingRule} onSuccess={handleClose} onCancel={handleClose} />
      </Modal>
    </Card>
  );
}

export default function SettingsEmail() {
  return (
    <div className="space-y-6">
      <AccountsSection />
      <RulesSection />
    </div>
  );
}
